/** DOM builders, one per screen. They read GameUi state and call back into it; no game logic here. */
import { MessageKey } from '../i18n';
import { PROGRESSION_CONFIG } from '../game/config';
import { LevelId } from '../sim/levels';
import { h, icon, stars } from './dom';
import { buildCredits } from './credits';
import { LevelMap } from './minimap';
import { bikeKey, helmetKey, kitKey } from './keys';
import { formatTime } from './logic/format';
import { LANG_LABEL, LANG_ORDER } from './logic/lang';
import { DRILL_ORDER, TIME_STARS, drillCards, levelCards, levelStats, nextDrill, obstacleList, suggestedLevel } from './logic/levels';
import { BIKES, HELMETS, KITS, hex } from './logic/livery';
import { QUALITY_OPTIONS } from './logic/settings';
import { ScreenId } from './logic/screens';
import { Ride } from './types';
import { FLEET, bikeForLevel, bikeSpec, BikeSpec, Discipline, disciplineOf } from '../sim/bikes';
import type { GameUi } from './ui';
import { videoButton } from './video';
import { filmNow, videoCountChip } from './share';

const LOGO = `${import.meta.env.BASE_URL}brand/megamotocross_logo_c.svg`;
export const SITE = 'https://megamotocross.pl';
export const PHONE = '+48 453 442 415';
export const WHATSAPP = 'https://wa.me/48453442415';

export function buildScreen(ui: GameUi, id: ScreenId): HTMLElement {
  switch (id) {
    case 'loading': return loading(ui);
    case 'start': return start(ui);
    case 'levels': return levels(ui);
    case 'drills': return drills(ui);
    case 'garage': return garage(ui);
    case 'intro': return intro(ui);
    case 'play': return play(ui);
    case 'pause': return pause(ui);
    case 'settings': return settings(ui);
    case 'results': return ui.replaying ? replayBar(ui) : results(ui);
    case 'credits': return buildCredits(ui);
  }
}

// ---------- shared pieces ----------

export function btn(label: string, onClick: () => void, opts: { cls?: string; ico?: string; primary?: boolean; disabled?: boolean; aria?: string } = {}): HTMLButtonElement {
  const cls = `btn${opts.primary ? '.primary' : ''}${opts.cls ? `.${opts.cls}` : ''}`;
  return h(`button.${cls}`, {
    attrs: { 'data-nav': '', disabled: opts.disabled, 'aria-label': opts.aria },
    on: { click: () => { if (!opts.disabled) onClick(); } },
  }, [opts.ico ? icon(opts.ico) : null, h('span', { text: label })]) as HTMLButtonElement;
}

export function topBar(ui: GameUi, title: string | null, opts: { back?: boolean } = {}): HTMLElement {
  return h('header.topbar', {}, [
    opts.back !== false ? h('button.icon-btn', { attrs: { 'data-nav': '', 'aria-label': ui.tr('ui.back'), title: ui.tr('ui.back') }, on: { click: () => ui.back() } }, [icon('back')]) : h('span'),
    title ? h('h1.title', {}, [h('span.title-bar'), h('span', { text: title })]) : h('span'),
    h('img.mini-logo', { attrs: { src: LOGO, alt: 'MegaMotocross' } }),
  ]);
}

function langSwitch(ui: GameUi): HTMLElement {
  return h('div.seg.lang', { attrs: { role: 'radiogroup', 'aria-label': ui.tr('ui.language') } }, LANG_ORDER.map((l) =>
    h(`button.seg-btn${l === ui.lang ? '.on' : ''}`, {
      text: LANG_LABEL[l], attrs: { 'data-nav': '', role: 'radio', 'aria-checked': l === ui.lang ? 'true' : 'false', lang: l === 'ua' ? 'uk' : l },
      on: { click: () => ui.updateSettings({ lang: l }) },
    })));
}

const levelName = (id: LevelId) => `level.${id}.name` as MessageKey;

function contactRow(ui: GameUi): HTMLElement {
  return h('div.contact', {}, [
    h('a.chip', { attrs: { href: SITE, target: '_blank', rel: 'noopener', 'data-nav': '' } }, [icon('web'), h('span', { text: 'megamotocross.pl' })]),
    h('a.chip', { attrs: { href: WHATSAPP, target: '_blank', rel: 'noopener', 'data-nav': '', 'aria-label': ui.tr('results.cta.whatsapp') } }, [icon('chat'), h('span', { text: 'WhatsApp' })]),
    h('a.chip', { attrs: { href: `tel:${PHONE.replace(/\s/g, '')}`, 'data-nav': '', 'aria-label': `${ui.tr('results.cta.call')} ${PHONE}` } }, [icon('phone'), h('span', { text: PHONE })]),
  ]);
}

// ---------- loading ----------

function loading(ui: GameUi): HTMLElement {
  const l = ui.loading;
  return h('section.screen.loading', {}, [
    h('div.stripes'),
    h('div.load-center', {}, [
      h('div.load-logo-wrap', {}, [h('img.load-logo', { attrs: { src: LOGO, alt: 'MegaMotocross', width: 1024, height: 998 } })]),
      h('div.brand-word', {}, [h('span.mega', { text: 'MEGAMOTOCROSS' }), h('span.sub', { text: ui.tr('ui.brand.tagline') })]),
      l.error
        ? h('div.load-error', { attrs: { role: 'alert' } }, [
          h('p', { text: ui.tr(l.error === 'network' ? 'ui.loading.errorNetwork' : 'ui.loading.error') }),
          // The technical reason, small: a player's screenshot of it tells us what failed on their device.
          l.detail ? h('p.load-detail', { text: l.detail }) : null,
          l.error === 'network' ? btn(ui.tr('ui.loading.retry'), () => location.reload(), { primary: true, ico: 'retry' }) : null,
        ])
        : h('div.load-block', {}, [
          h('div.load-bar', { attrs: { role: 'progressbar', 'aria-label': ui.tr(l.stage), 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-valuenow': Math.round(l.progress * 100) } },
            [h('i', { style: { transform: `scaleX(${l.progress})` } })]),
          h('div.load-meta', {}, [h('span.load-stage', { text: ui.tr(l.stage) }), h('span.load-pct', { text: `${Math.round(l.progress * 100)}%` })]),
        ]),
    ]),
    h('p.load-tip', { text: ui.tr(ui.tip) }),
  ]);
}

// ---------- start ----------

function start(ui: GameUi): HTMLElement {
  const host = ui.host!;
  const p = host.progress();
  const next = suggestedLevel(p);
  const cards = levelCards(p);
  const total = cards.reduce((a, c) => a + c.stars, 0), max = cards.reduce((a, c) => a + c.maxStars, 0);
  // Ride always opens the level choice; the suggested level is highlighted there, the player picks.
  const ride = (): void => ui.go('levels');
  return h('section.screen.start', {}, [
    h('div.start-top', {}, [langSwitch(ui), h('div.star-total', { attrs: { 'aria-label': ui.tr('ui.stars', { n: total, max }) } }, [icon('star'), h('span', { text: `${total}/${max}` })]), videoCountChip(ui)]),
    h('div.start-hero', {}, [
      h('img.hero-logo', { attrs: { src: LOGO, alt: 'MegaMotocross', width: 1024, height: 998 } }),
      h('div.hero-text', {}, [
        h('div.eyebrow', { text: ui.tr('ui.brand.tagline') }),
        h('h1.hero-title', {}, [h('span', { text: 'MEGA' }), h('span.accent', { text: 'MOTOCROSS' })]),
        h('p.hero-sub', { text: ui.tr('ui.brand.sub') }),
      ]),
    ]),
    h('nav.start-menu', {}, [
      h('button.btn.primary.xl', { attrs: { 'data-nav': '', autofocus: true }, on: { click: ride } }, [
        icon('play'), h('span.stack', {}, [h('span', { text: ui.tr('ui.ride') }), h('small', { text: ui.tr('ui.rideNext', { level: ui.tr(levelName(next)) }) })]),
      ]),
      h('div.menu-row', {}, [
        btn(ui.tr('ui.levels'), () => ui.go('levels'), { ico: 'flag' }),
        btn(ui.tr('ui.garage'), () => ui.go('garage'), { ico: 'garage' }),
        btn(ui.tr('ui.settings'), () => ui.go('settings'), { ico: 'gear' }),
        btn(ui.tr('ui.credits'), () => ui.go('credits'), { ico: 'list' }),
      ]),
    ]),
    h('footer.start-foot', {}, [contactRow(ui), career(ui)]),
  ]);
}

function career(ui: GameUi): HTMLElement {
  const cards = levelCards(ui.host!.progress());
  return h('div.career', {}, cards.map((c, i) => h(`button.career-card${c.locked ? '.locked' : ''}`, {
    attrs: { 'data-nav': '', 'aria-label': `${ui.tr(levelName(c.id))}${c.locked ? `, ${ui.tr('ui.locked')}` : ''}` },
    on: { click: () => ui.go('levels') },
  }, [
    new LevelMap(c.id, 120, 80, 'map mini-map').svg,
    h('div.career-text', {}, [
      h('span.career-num', { text: `0${i + 1}` }),
      h('b', { text: ui.tr(levelName(c.id)) }),
      c.locked ? h('span.career-lock', {}, [icon('lock'), h('span', { text: ui.tr('ui.locked') })]) : stars(Math.round((c.stars / c.maxStars) * 3), 3, false, 'sm'),
    ]),
  ])));
}

const kmText = (ui: GameUi, km: number) => String(km).replace('.', ui.lang === 'en' ? '.' : ',');

/** Card description with its numbers (length, checkpoints, obstacles per kind) taken from the level data. */
function levelDesc(ui: GameUi, id: LevelId): string {
  const st = levelStats(id);
  const obstacles = obstacleList(st.kinds, ui.tr);
  return ui.tr(`ui.level.${id}.desc` as MessageKey, {
    km: st.km === null ? '' : kmText(ui, st.km), cp: st.checkpoints, obstacles, laps: PROGRESSION_CONFIG.timeTrialLaps,
  });
}

function statChips(ui: GameUi, id: LevelId): HTMLElement {
  const st = levelStats(id);
  const chips = [ui.tr(`ui.stat.surface.${id}` as MessageKey)];
  if (st.km !== null) chips.push(ui.tr('ui.stat.length', { km: kmText(ui, st.km) }));
  if (st.checkpoints) chips.push(ui.tr('ui.stat.checkpoints', { n: st.checkpoints }));
  if (st.features) chips.push(ui.tr('ui.stat.features', { n: st.features }));
  return h('div.stat-chips', {}, chips.map((c) => h('span.stat', { text: c })));
}

// ---------- levels ----------

function levels(ui: GameUi): HTMLElement {
  const p = ui.host!.progress();
  const suggested = suggestedLevel(p);
  const cards = levelCards(p).map((c, i) => {
    const name = ui.tr(levelName(c.id));
    const bike = bikeSpec(bikeForLevel(c.id, ui.settings));
    const body: (HTMLElement | null)[] = [
      h('div.card-num', { text: `0${i + 1}` }),
      h('h2.card-title', { text: name }),
      h('p.card-desc', { text: levelDesc(ui, c.id) }),
      statChips(ui, c.id),
      h('div.card-bike', { attrs: { title: ui.tr('ui.level.bike') } }, [icon('garage'), h('span.muted', { text: ui.tr('ui.level.bike') }), h('b', { text: `${bike.brand} ${bike.name}` })]),
      h('div.card-meta', {}, [
        stars(Math.round((c.stars / c.maxStars) * 3), 3, false, 'sm'),
        h('span.meta-txt', { text: c.id === 'ring' ? `${c.stars}/9 · ${ui.tr('ui.level.drillsCount')}` : c.best !== null ? ui.tr('ui.best', { time: formatTime(c.best) }) : '' }),
      ]),
    ];
    let actions: HTMLElement;
    if (c.locked) {
      actions = h('div.lock-note', {}, [icon('lock'), h('span', { text: ui.tr(c.hint!, { time: PROGRESSION_CONFIG.enduroLapTime }) })]);
    } else if (c.id === 'ring') {
      actions = h('div.card-actions', {}, [
        btn(ui.tr('ui.select'), () => ui.go('drills'), { primary: true, ico: 'next' }),
        btn(ui.tr('ui.mode.oval'), () => ui.startRide({ level: 'ring', kind: 'oval' }), { ico: 'flag' }),
      ]);
    } else if (c.id === 'track') {
      actions = h('div.card-actions', {}, [
        btn(ui.tr('ui.mode.trial', { laps: PROGRESSION_CONFIG.timeTrialLaps }), () => ui.startRide({ level: 'track', kind: 'trial' }), { primary: true, ico: 'timer' }),
        btn(ui.tr('ui.mode.free'), () => ui.startRide({ level: 'track', kind: 'free' }), { ico: 'flag' }),
      ]);
    } else {
      actions = h('div.card-actions', {}, [btn(ui.tr('ui.mode.enduro'), () => ui.startRide({ level: 'enduro', kind: 'enduro' }), { primary: true, ico: 'flag' })]);
    }
    // The suggested level: highlighted, and keyboard / gamepad focus starts on its first button.
    const isNext = c.id === suggested && !c.locked;
    if (isNext) actions.querySelector('button')?.setAttribute('autofocus', '');
    return h(`article.level-card.lv-${c.id}${c.locked ? '.locked' : ''}${isNext ? '.suggested' : ''}`, { attrs: { 'aria-label': `${name}${isNext ? `, ${ui.tr('ui.level.suggested')}` : ''}${c.locked ? `, ${ui.tr('ui.locked')}` : ''}` } }, [
      h('div.card-art', {}, [h('div.art-lines'), new LevelMap(c.id, 320, 180, 'map card-map').svg, isNext ? h('span.suggest-badge', {}, [icon('play'), h('span', { text: ui.tr('ui.level.suggested') })]) : null]),
      h('div.card-body', {}, [...body, actions]),
      c.locked ? h('div.lock-badge', {}, [icon('lock'), h('span', { text: ui.tr('ui.locked') })]) : null,
    ]);
  });
  return h('section.screen.levels', {}, [topBar(ui, ui.tr('ui.levels')), h('div.card-row', {}, cards)]);
}

// ---------- drills ----------

function drills(ui: GameUi): HTMLElement {
  const cards = drillCards(ui.host!.progress()).map((d, i) =>
    h(`button.drill-card${d.next ? '.next' : ''}`, {
      attrs: { 'data-nav': '', 'aria-label': `${ui.tr(`drill.${d.id}.name` as MessageKey)}, ${ui.tr('ui.stars', { n: d.stars, max: 3 })}` },
      on: { click: () => ui.startRide({ level: 'ring', kind: 'drill', drill: d.id }) },
    }, [
      h('div.plate', {}, [h('span', { text: String(i + 1) })]),
      h('div.drill-text', {}, [
        h('div.eyebrow', { text: ui.tr('ui.drills.step', { n: i + 1 }) }),
        h('h2.card-title', { text: ui.tr(`drill.${d.id}.name` as MessageKey) }),
        h('p.card-desc', { text: ui.tr(`drill.${d.id}.description` as MessageKey) }),
      ]),
      stars(d.stars),
      d.next ? h('span.ribbon', { text: ui.tr('ui.drills.next') }) : null,
    ]));
  // Free ride on the training oval, after the drills: no stars, no clock.
  cards.push(h('button.drill-card.oval', {
    attrs: { 'data-nav': '', 'aria-label': ui.tr('ui.mode.oval') },
    on: { click: () => ui.startRide({ level: 'ring', kind: 'oval' }) },
  }, [
    h('div.plate', {}, [icon('flag')]),
    h('div.drill-text', {}, [
      h('div.eyebrow', { text: ui.tr('ui.mode.oval') }),
      h('h2.card-title', { text: ui.tr('ui.oval.name') }),
      h('p.card-desc', { text: ui.tr('ui.oval.desc') }),
    ]),
  ]));
  const p = ui.host!.progress();
  const passed = DRILL_ORDER.filter((d) => p.drills[d] >= PROGRESSION_CONFIG.trackMinStars).length;
  const track = levelCards(p)[1]!;
  const meter = h('aside.panel.glass.unlock-meter', {}, [
    new LevelMap('track', 220, 140, 'map meter-map').svg,
    h('div.eyebrow', { text: ui.tr('level.track.name') }),
    h('div.meter-num', {}, [h('b', { text: String(passed) }), h('span', { text: `/ ${DRILL_ORDER.length}` })]),
    h('div.prog-bar.big', {}, [h('i', { style: { width: `${(passed / DRILL_ORDER.length) * 100}%` } })]),
    h('p.muted.small', { text: track.locked ? ui.tr('unlock.trackHint') : ui.tr('unlock.track') }),
  ]);
  return h('section.screen.drills', {}, [topBar(ui, ui.tr('ui.drills.title')), h('div.drills-layout', {}, [h('div.drill-list', {}, cards), meter])]);
}

// ---------- garage ----------

function garage(ui: GameUi): HTMLElement {
  const s = ui.settings;
  const swatches = (list: typeof BIKES, sel: string, key: (id: string) => MessageKey, pick: (id: string) => void) =>
    h('div.swatches', { attrs: { role: 'radiogroup' } }, list.map((l) => h(`button.swatch${l.id === sel ? '.on' : ''}`, {
      attrs: { 'data-nav': '', role: 'radio', 'aria-checked': l.id === sel ? 'true' : 'false', 'aria-label': ui.tr(key(l.id)), title: ui.tr(key(l.id)) },
      style: { '--c1': hex(l.primary), '--c2': hex(l.secondary) },
      on: { click: () => { pick(l.id); ui.render(); } },
    })));
  // School rule: every bike rides one discipline. The loaded level's group comes first; the other is greyed, and a
  // pick there is kept for when that kind of level is ridden.
  const here = disciplineOf(ui.host?.loadedLevel ?? 'ring');
  const pick: Record<Discipline, string> = { track: s.model, enduro: s.enduroModel };
  const spec = bikeSpec(garageViewed && FLEET.some((b) => b.id === garageViewed) ? garageViewed : pick[here]);
  const groups = (here === 'track' ? ['track', 'enduro'] : ['enduro', 'track']) as Discipline[];
  ui.host?.previewBike?.(spec.id); // the 3D bike follows the spec sheet (no-op when it already shows it)
  return h(`section.screen.garage${garageLoading ? '.loading' : ''}`, {}, [
    topBar(ui, ui.tr('ui.garage')),
    specCard(ui, spec),
    h('div.bike-spinner', { attrs: { role: 'status', 'aria-label': ui.tr('garage.loading') } }, [h('i'), h('span', { text: ui.tr('garage.loading') })]),
    h('aside.panel.glass.garage-panel', {}, [
      h('div.eyebrow', { text: ui.tr('garage.fleet') }),
      ...groups.map((d) => h(`div.fleet-group${d === here ? '' : '.off-level'}`, {}, [
        h('h3.section', { text: ui.tr(`garage.group.${d}` as MessageKey) }),
        d === here ? null : h('p.muted.small', { text: ui.tr(`garage.offLevel.${d}` as MessageKey) }),
        h('div.fleet', { attrs: { role: 'radiogroup', 'aria-label': ui.tr(`garage.group.${d}` as MessageKey) } },
          FLEET.filter((b) => b.discipline === d).map((b) => fleetCard(ui, b, b.id === pick[d], b.id === spec.id))),
      ])),
      specSheet(ui, spec),
      // Plastics re-tint the KTM model; the other fleet models carry their brand livery.
      ...(spec.model.file ? [] : [
        h('h3.section', { text: ui.tr('garage.bike') }),
        swatches(BIKES, s.bike, bikeKey, (id) => ui.updateSettings({ bike: id })),
        h('p.sel-name', { text: ui.tr(bikeKey(s.bike)) }),
      ]),
      h('h3.section', { text: ui.tr('garage.kit') }),
      swatches(KITS, s.kit, kitKey, (id) => ui.updateSettings({ kit: id })),
      h('p.sel-name', { text: ui.tr(kitKey(s.kit)) }),
      h('h3.section', { text: ui.tr('garage.helmet') }),
      swatches(HELMETS, s.helmet, helmetKey, (id) => ui.updateSettings({ helmet: id })),
      h('p.sel-name', { text: ui.tr(helmetKey(s.helmet)) }),
      h('p.muted.small', { text: ui.tr('garage.note') }),
      btn(ui.tr('ui.back'), () => ui.back(), { primary: true, ico: 'back' }),
    ]),
  ]);
}

/** The bike whose spec sheet the garage shows (the last one clicked; the loaded level's pick until then). */
let garageViewed: string | null = null;

/** The bike whose model is still loading (the garage shows a spinner); null when the shown bike is the pick. */
let garageLoading: string | null = null;
/** The bike the spec card showed last: the card animates only when it changes. */
let cardShown: string | null = null;

/** Spinner while a picked bike loads; a cached swap resolves at once and never shows it (it also fades in late). */
function showSwap(p: Promise<void> | void, id: string): void {
  if (!p) return;
  garageLoading = id;
  void p.finally(() => {
    if (garageLoading !== id) return; // a later pick owns the spinner
    garageLoading = null;
    document.querySelector('.screen.garage')?.classList.remove('loading');
  });
}

/** Fleet range of a number, for the stat bars (each bar reads against the rest of the school fleet). */
function fleetRange(f: (b: BikeSpec) => number): [number, number] {
  const v = FLEET.map(f);
  return [Math.min(...v), Math.max(...v)];
}

/** The selected bike's headline card beside the 3D bike: name, badges, stat bars against the fleet, who it is for. */
function specCard(ui: GameUi, b: BikeSpec): HTMLElement {
  const changed = cardShown !== b.id;
  cardShown = b.id;
  const livery = BIKES.find((l) => l.id === b.livery) ?? BIKES[0]!;
  const n = (v: number) => v.toLocaleString(ui.lang === 'ua' ? 'uk' : ui.lang);
  const stat = (label: MessageKey, value: string, f: (s: BikeSpec) => number) => {
    const [lo, hi] = fleetRange(f);
    const t = hi > lo ? 0.18 + 0.82 * ((f(b) - lo) / (hi - lo)) : 1;
    return h('div.sc-stat', {}, [
      h('span.sc-label', { text: ui.tr(label) }),
      h('span.sc-val', { text: value }),
      h('span.sc-bar', {}, [h('i', { style: { '--t': t.toFixed(3) } })]),
    ]);
  };
  return h(`aside.spec-card.glass${changed ? '.swap' : ''}`, { style: { '--c1': hex(livery.primary) }, attrs: { 'aria-live': 'polite' } }, [
    h('div.sc-brand', { text: b.brand }),
    h('h2.sc-name', { text: b.name }),
    h('div.sc-tags', {}, [
      h(`span.tag.stroke-${b.stroke.toLowerCase()}`, { text: ui.tr(`garage.stroke.${b.stroke}` as MessageKey) }),
      h(`span.tag.disc-${b.discipline}`, { text: ui.tr(`garage.disc.${b.discipline}` as MessageKey) }),
      h(`span.tag.lvl-${b.level}`, { text: ui.tr(`garage.level.${b.level}` as MessageKey) }),
      h('span.tag', { text: String(b.year) }),
    ]),
    h('div.sc-stats', {}, [
      stat('garage.spec.power', ui.tr('garage.short.kw', { kw: n(b.peakPower) }), (s) => s.peakPower),
      stat('garage.spec.weight', ui.tr('garage.short.kg', { kg: n(b.dryMass) }), (s) => s.dryMass),
      stat('garage.spec.cc', ui.tr('garage.short.cc', { cc: b.displacement }), (s) => s.displacement),
      stat('garage.spec.seat', ui.tr('garage.val.mm', { mm: b.seatHeight }), (s) => s.seatHeight),
      stat('garage.spec.gears', ui.tr('garage.val.gears', { n: b.gearbox.length }), (s) => s.gearbox.length),
    ]),
    h('div.sc-for', {}, [h('b', { text: `${ui.tr('garage.for')}: ` }), h('span', { text: ui.tr(`bike.${b.id}.for` as MessageKey) })]),
    b.model.approximate ? h('div.sc-approx.muted', { text: `${ui.tr('garage.approx')}: ${ui.tr('garage.approxHint')}` }) : null,
  ]);
}

/** One fleet bike in the garage list: brand, model, engine badge, discipline and the school's rider level. */
function fleetCard(ui: GameUi, b: BikeSpec, on: boolean, viewed: boolean): HTMLElement {
  const livery = BIKES.find((l) => l.id === b.livery) ?? BIKES[0]!;
  return h(`button.fleet-card${on ? '.on' : ''}${viewed ? '.viewed' : ''}`, {
    attrs: { 'data-nav': '', role: 'radio', 'aria-checked': on ? 'true' : 'false', 'data-bike': b.id },
    style: { '--c1': hex(livery.primary) },
    // The pick for the bike's discipline; a track bike also puts on its brand plastics (the swatches can change them).
    on: { click: () => { garageViewed = b.id; showSwap(ui.host?.previewBike?.(b.id), b.id); ui.updateSettings(b.discipline === 'enduro' ? { enduroModel: b.id } : { model: b.id, bike: b.livery }); ui.render(); } },
  }, [
    h('span.fleet-dot'),
    h('span.fleet-name', {}, [h('b', { text: b.brand }), h('span', { text: ` ${b.name}` })]),
    h(`span.tag.stroke-${b.stroke.toLowerCase()}`, { text: b.stroke }),
    h(`span.tag.disc-${b.discipline}`, { text: ui.tr(`garage.disc.${b.discipline}` as MessageKey) }),
    h(`span.tag.lvl-${b.level}`, { text: ui.tr(`garage.level.${b.level}` as MessageKey) }),
  ]);
}

/**
 * Torque against rpm for the selected bike over the rest of the fleet (same axes for all, so a peaky 125 2-stroke
 * and a flat 350 4-stroke read at a glance). Static markup built from the spec numbers.
 */
function torqueChart(ui: GameUi, b: BikeSpec): HTMLElement {
  const W = 300, H = 96, RPM = 15000, NM = 40, L = 26, B = 16;
  const x = (rpm: number) => L + (rpm / RPM) * (W - L - 4), y = (nm: number) => H - B - (nm / NM) * (H - B - 6);
  const path = (s: BikeSpec) => s.torqueCurve.map(([r, f], i) => `${i ? 'L' : 'M'}${x(r).toFixed(1)},${y(f * s.peakTorque).toFixed(1)}`).join('');
  const others = FLEET.filter((s) => s.id !== b.id).map((s) => `<path d="${path(s)}" class="tc-other"/>`).join('');
  const grid = [10, 20, 30].map((nm) => `<line x1="${L}" x2="${W - 4}" y1="${y(nm)}" y2="${y(nm)}" class="tc-grid"/><text x="${L - 4}" y="${y(nm) + 3}" class="tc-lab" text-anchor="end">${nm}</text>`).join('')
    + [5000, 10000, 15000].map((r) => `<text x="${x(r)}" y="${H - 3}" class="tc-lab" text-anchor="middle">${r / 1000}k</text>`).join('');
  const peak = `<circle cx="${x(b.peakTorqueRpm)}" cy="${y(b.peakTorque)}" r="3" class="tc-peak"/>`;
  return h('figure.torque-chart', {}, [
    h('figcaption.muted.small', { text: ui.tr('garage.curve') }),
    h('div', { html: `<svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${ui.tr('garage.curve')}">${grid}${others}<path d="${path(b)}" class="tc-this"/>${peak}</svg>` }),
  ]);
}

/** Spec sheet of the selected bike; values without a manufacturer figure carry a star. */
function specSheet(ui: GameUi, b: BikeSpec): HTMLElement {
  const est = (k: keyof BikeSpec) => (b.estimated.includes(k) ? ' *' : '');
  const n = (v: number) => v.toLocaleString(ui.lang === 'ua' ? 'uk' : ui.lang);
  const row = (label: MessageKey, value: string) => h('div.spec-row', {}, [h('span.muted', { text: ui.tr(label) }), h('span', { text: value })]);
  return h('div.spec-sheet', {}, [
    h('h3.section', { text: ui.tr('garage.details') }),
    h('div.spec-grid', {}, [
      row('garage.spec.engine', ui.tr('garage.val.engine', { cc: b.displacement, stroke: ui.tr(`garage.stroke.${b.stroke}` as MessageKey) })),
      row('garage.spec.power', ui.tr('garage.val.power', { kw: n(b.peakPower), rpm: n(b.peakPowerRpm) }) + est('peakPower')),
      row('garage.spec.torque', ui.tr('garage.val.torque', { nm: n(b.peakTorque), rpm: n(b.peakTorqueRpm) }) + est('peakTorque')),
      row('garage.spec.weight', ui.tr('garage.val.weight', { kg: n(b.dryMass) }) + est('dryMass')),
      row('garage.spec.gears', ui.tr('garage.val.gears', { n: b.gearbox.length }) + est('gearbox')),
      row('garage.spec.travel', ui.tr('garage.val.travel', { front: b.travelFront, rear: b.travelRear })),
      row('garage.spec.seat', ui.tr('garage.val.mm', { mm: b.seatHeight })),
      row('garage.spec.wheelbase', ui.tr('garage.val.mm', { mm: b.wheelbase })),
    ]),
    torqueChart(ui, b),
    b.estimated.length ? h('div.muted.small', { text: ui.tr('garage.estimated') }) : null,
  ]);
}

// ---------- intro ----------

interface IntroText { title: string; goal: MessageKey; scoring: MessageKey; hint: MessageKey; level: LevelId }
/** Star thresholds for the intro's scoring line, from the same constants the session scores with. */
function scoringParams(level: LevelId): Record<string, number> {
  if (level === 'track') return { three: TIME_STARS.track[1], two: TIME_STARS.track[0], unlock: PROGRESSION_CONFIG.enduroLapTime };
  if (level === 'enduro') return { three: TIME_STARS.enduro[1] / 60, two: TIME_STARS.enduro[0] / 60 };
  return {};
}

function introText(ui: GameUi, r: Ride): IntroText {
  if (r.kind === 'drill') {
    return { title: ui.tr(`drill.${r.drill}.name` as MessageKey), goal: `drill.${r.drill}.description` as MessageKey, scoring: `drill.${r.drill}.scoring` as MessageKey, hint: `drill.${r.drill}.hint` as MessageKey, level: 'ring' };
  }
  if (r.kind === 'oval') return { title: `${ui.tr('ui.oval.name')} · ${ui.tr('ui.mode.free')}`, goal: 'ui.intro.oval.goal', scoring: 'ui.intro.oval.scoring', hint: 'ui.tip.2', level: 'ring' };
  if (r.kind === 'enduro') return { title: ui.tr('level.enduro.name'), goal: 'ui.intro.enduro.goal', scoring: 'ui.intro.enduro.scoring', hint: 'ui.intro.enduro.hint', level: 'enduro' };
  if (r.kind === 'free') return { title: `${ui.tr('level.track.name')} · ${ui.tr('ui.mode.free')}`, goal: 'ui.intro.free.goal', scoring: 'ui.intro.free.scoring', hint: 'ui.intro.track.hint', level: 'track' };
  return { title: `${ui.tr('level.track.name')} · ${ui.tr('ui.mode.trial', { laps: PROGRESSION_CONFIG.timeTrialLaps })}`, goal: 'ui.intro.track.goal', scoring: 'ui.intro.track.scoring', hint: 'ui.intro.track.hint', level: 'track' };
}

export function controlsTable(ui: GameUi): HTMLElement {
  const k = (s: string) => h('kbd', { text: s });
  // The default scheme (owner decision A): throttle, one brake (the game splits it and keeps the wheels from locking),
  // steer and lean back / forward (the trick key); stance and foot are the game's. Pro controls: both brakes, body,
  // stance and the foot key.
  const auto = ui.tr('ctrl.auto');
  const stick = ui.tr('ctrl.pad.lstick');
  const rows: [MessageKey, string[], string[], string][] = ui.settings.beginner ? [
    ['ctrl.throttle', ['W'], ['RT'], ui.tr('touch.gas')],
    ['ctrl.brakeSimplified', ['S'], ['LT'], ui.tr('touch.brake')],
    ['ctrl.steer', ['A', 'D'], [`${stick} ↔`], `${ui.tr('ctrl.touch.stick')} ↔`],
    ['ctrl.leanBack', ['Q', '↓'], [`${stick} ↓`], `${ui.tr('ctrl.touch.stick')} ↓`],
    ['ctrl.leanForward', ['E', '↑'], [`${stick} ↑`], `${ui.tr('ctrl.touch.stick')} ↑`],
    ['ctrl.stand', [], [], auto],
    ['ctrl.foot', [], [], auto],
    ['ctrl.reverse', ['S'], ['B'], ui.tr('touch.brake')],
    ['ctrl.reset', ['R'], ['Y'], ui.tr('touch.reset')],
    ['ctrl.camera', ['C'], ['View', 'R3'], '◉'],
    ['ctrl.pause', ['Esc'], ['Start'], '❚❚'],
  ] : [
    ['ctrl.throttle', ['W'], ['RT'], ui.tr('touch.gas')],
    ['ctrl.frontBrake', ['Shift'], ['LT'], ui.tr('touch.front')],
    ['ctrl.rearBrake', ['S'], ['B'], ui.tr('touch.rear')],
    ['ctrl.steer', ['A', 'D'], [ui.tr('ctrl.pad.lstick')], `${ui.tr('ctrl.touch.stick')} ↔`],
    ['ctrl.body', ['E', 'Q'], [ui.tr('ctrl.pad.rstick')], `${ui.tr('ctrl.touch.stick')} ↕`],
    ['ctrl.stand', ['Space'], ['A'], ui.tr('touch.stand')],
    ['ctrl.foot', ['F'], ['X'], ui.tr('touch.foot')],
    ['ctrl.reverse', ['S'], ['B'], ui.tr('touch.rear')],
    ['ctrl.reset', ['R'], ['Y'], ui.tr('touch.reset')],
    ['ctrl.camera', ['C'], ['View', 'R3'], '◉'],
    ['ctrl.pause', ['Esc'], ['Start'], '❚❚'],
  ];
  const order = ui.touch ? [2, 0, 1] : [0, 1, 2];
  const heads = [['keyboard', 'ctrl.keyboard'], ['pad', 'ctrl.gamepad'], ['touch', 'ctrl.touch']] as const;
  // Touch devices: only the on-screen controls, as a plain list. A phone shows no keyboard or gamepad columns (the
  // player has neither); the keyboard/pad table stays for the desktop pause, intro and settings screens.
  if (ui.touch) {
    return h('div.controls-touch', {}, rows.map(([label, , , touch]) =>
      h('div.ctrl-touch-row', {}, [h('span', { text: ui.tr(label) }), h('b', { text: touch })])));
  }
  return h('table.controls', {}, [
    h('thead', {}, [h('tr', {}, [h('th'), ...order.map((i) => h('th', {}, [icon(heads[i]![0]), h('span', { text: ui.tr(heads[i]![1]) })]))])]),
    h('tbody', {}, rows.map(([label, kb, pad, touch]) => {
      const auto = !kb.length ? [h('span.muted', { text: ui.tr('ctrl.auto') })] : null;
      const cells = [h('td', {}, auto ?? kb.map(k)), h('td', {}, auto ?? pad.map((p) => (p.length <= 5 ? k(p) : h('span', { text: p })))), h('td', {}, [h('span', { text: touch })])];
      return h('tr', {}, [h('th', { text: ui.tr(label) }), ...order.map((i) => cells[i]!)]);
    })),
  ]);
}

/**
 * "Pro controls (advanced)", Settings only (owner decision A: one scheme, no visible mode choice elsewhere). Off =
 * the default assisted scheme; on = separate brakes, manual stance and foot, tricks by hand. Saved at once.
 */
export function proControlsSwitch(ui: GameUi): HTMLElement {
  const pro = !ui.settings.beginner;
  const opt = (on: boolean, key: MessageKey) => h(`button.seg-btn${on === pro ? '.on' : ''}`, {
    text: ui.tr(key), attrs: { 'data-nav': '', role: 'radio', 'aria-checked': on === pro ? 'true' : 'false' },
    on: { click: () => { ui.updateSettings({ beginner: !on }); ui.render(); } },
  });
  return h('div.control-mode', {}, [
    h('h3.section', {}, [icon('pad'), h('span', { text: ui.tr('settings.pro') })]),
    h('div.seg', { attrs: { role: 'radiogroup', 'aria-label': ui.tr('settings.pro') } }, [opt(false, 'settings.off'), opt(true, 'settings.on')]),
    h('p.muted.small', { text: ui.tr(pro ? 'settings.controlModeHint.real' : 'settings.controlModeHint.simplified') }),
  ]);
}

function coachAvatar(): HTMLElement {
  return h('div.avatar', {}, [h('img', { attrs: { src: LOGO, alt: '' } })]);
}

function intro(ui: GameUi): HTMLElement {
  const r = ui.ride ?? { level: 'ring', kind: 'drill', drill: 'throttle' } as Ride;
  const x = introText(ui, r);
  const go = () => ui.startRide(r);
  return h('section.screen.intro', {}, [
    topBar(ui, null),
    h('div.panel.glass.intro-card', {}, [
      h('div.intro-head', {}, [
        h('div', {}, [h('div.eyebrow', { text: ui.tr(levelName(x.level)) }), h('h1.intro-title', { text: x.title })]),
        new LevelMap(x.level, 180, 110, 'map intro-map').svg,
      ]),
      h('div.intro-grid', {}, [
        h('div.intro-col', {}, [
          h('h3.section', {}, [icon('flag'), h('span', { text: ui.tr('ui.intro.goal') })]),
          h('p.lead', { text: ui.tr(x.goal) }),
          h('h3.section', {}, [icon('trophy'), h('span', { text: ui.tr('ui.intro.scoring') })]),
          h('p', { text: ui.tr(x.scoring, scoringParams(x.level)) }),
          h('div.coach-tip', {}, [coachAvatar(), h('div', {}, [h('div.eyebrow', { text: ui.tr('ui.intro.tip') }), h('p', { text: ui.tr(x.hint) })])]),
        ]),
        h('div.intro-col', {}, [h('h3.section', {}, [icon('pad'), h('span', { text: ui.tr('ui.intro.controls') })]), controlsTable(ui)]),
      ]),
      h('div.intro-actions', {}, [
        h('button.btn.primary.xl.go', { attrs: { 'data-nav': '', autofocus: true }, on: { click: go } }, [icon('play'), h('span', { text: ui.tr('ui.go') })]),
      ]),
    ]),
  ]);
}

// ---------- play / pause ----------

function play(ui: GameUi): HTMLElement {
  return h('section.screen.play', {}, [
    h('div.fpv-goggles', { attrs: { 'aria-hidden': 'true' } }),
    // Labelled with the view it switches TO, so the option is obvious: "First person (C)" / "Chase view (C)".
    h('button.btn.camera-btn', { attrs: { 'data-nav': '', 'aria-label': ui.tr('hud.camera'), title: `${ui.tr('hud.camera')} (C)` }, on: { click: (e: Event) => { ui.toggleCamera(); (e.currentTarget as HTMLElement).blur(); } } }, [
      icon('camera'),
      h('span.cam-label', { text: ui.tr(ui.settings.camera === 'fpv' ? 'hud.cam.chase' : 'hud.cam.fpv') }),
      h('kbd.cam-key', { text: 'C' }),
    ]),
    h('button.icon-btn.pause-btn', { attrs: { 'data-nav': '', 'aria-label': ui.tr('hud.pause'), title: ui.tr('hud.pause') }, on: { click: () => ui.dispatch({ type: 'pause' }) } }, [icon('pause')]),
  ]);
}

function pause(ui: GameUi): HTMLElement {
  return h('section.screen.pause', {}, [
    h('div.panel.glass.pause-controls', {}, [h('h3.section', {}, [icon('pad'), h('span', { text: ui.tr('ui.intro.controls') })]), controlsTable(ui)]),
    h('div.panel.glass.pause-card', {}, [
      h('h1.title.center', {}, [h('span.title-bar'), h('span', { text: ui.tr('ui.pause.title') })]),
      h('div.stack-btns', {}, [
        btn(ui.tr('ui.resume'), () => ui.dispatch({ type: 'resume' }), { primary: true, ico: 'play' }),
        btn(ui.tr('ui.restart'), () => ui.dispatch({ type: 'restart' }), { ico: 'retry' }),
        // Any mode, free ride included, once 15 s are recorded: filming keeps the ride and resumes it here.
        ui.host?.canMakeVideo?.() ? btn(ui.tr('video.create'), () => filmNow(ui), { ico: 'camera', cls: 'video-btn pause-video' }) : null,
        btn(ui.tr('ui.settings'), () => ui.go('settings'), { ico: 'gear' }),
        btn(ui.tr('ui.quit'), () => ui.go('levels'), { ico: 'back' }),
      ]),
    ]),
  ]);
}

// ---------- settings ----------

function settings(ui: GameUi): HTMLElement {
  const s = ui.settings;
  const seg = <T extends string>(opts: readonly T[], cur: T, label: (o: T) => string, set: (o: T) => void) =>
    h('div.seg', { attrs: { role: 'radiogroup' } }, opts.map((o) => h(`button.seg-btn${o === cur ? '.on' : ''}`, {
      text: label(o), attrs: { 'data-nav': '', role: 'radio', 'aria-checked': o === cur ? 'true' : 'false' },
      on: { click: () => { set(o); ui.render(); } },
    })));
  const slider = (key: 'master' | 'engine' | 'sfx' | 'ambience', label: MessageKey) => {
    const out = h('output', { text: `${Math.round(s[key] * 100)}` });
    return h('label.slider', {}, [
      h('span', { text: ui.tr(label) }),
      h('input', { attrs: { type: 'range', min: 0, max: 100, step: 5, value: Math.round(s[key] * 100), 'data-nav': '' }, on: {
        input: (e) => { const v = Number((e.target as HTMLInputElement).value) / 100; out.textContent = String(Math.round(v * 100)); ui.updateSettings({ [key]: v }); },
      } }),
      out,
    ]);
  };
  let armed = false;
  const reset = btn(ui.tr('settings.resetProgress'), () => {
    if (!armed) { armed = true; reset.querySelector('span')!.textContent = ui.tr('settings.resetConfirm'); reset.classList.add('danger'); return; }
    ui.host!.resetProgress();
    reset.querySelector('span')!.textContent = ui.tr('settings.resetDone');
    reset.disabled = true;
  }, { cls: 'ghost' });
  return h('section.screen.settings', {}, [
    topBar(ui, ui.tr('ui.settings')),
    h('div.settings-grid', {}, [
      h('div.panel.glass', {}, [
        h('h3.section', {}, [icon('web'), h('span', { text: ui.tr('ui.language') })]),
        langSwitch(ui),
        h('h3.section', {}, [icon('gear'), h('span', { text: ui.tr('settings.quality') })]),
        seg(QUALITY_OPTIONS, s.quality, (o) => ui.tr(`settings.quality.${o}` as MessageKey), (o) => ui.updateSettings({ quality: o })),
        h('p.muted.small', { text: ui.tr('settings.qualityNote') }),
        h('h3.section', {}, [icon('sound'), h('span', { text: ui.tr('settings.audio') })]),
        slider('master', 'settings.master'), slider('engine', 'settings.engine'), slider('sfx', 'settings.sfx'), slider('ambience', 'settings.ambience'),
        h('h3.section', {}, [icon('info'), h('span', { text: ui.tr('settings.hints') })]),
        seg(['on', 'off'] as const, s.hints ? 'on' : 'off', (o) => ui.tr(`settings.${o}` as MessageKey), (o) => ui.updateSettings({ hints: o === 'on' })),
        h('h3.section', {}, [icon('camera'), h('span', { text: ui.tr('settings.clipTips') })]),
        seg(['on', 'off'] as const, s.clipTips ? 'on' : 'off', (o) => ui.tr(`settings.${o}` as MessageKey), (o) => ui.updateSettings({ clipTips: o === 'on' })),
        proControlsSwitch(ui),
        reset,
      ]),
      h('div.panel.glass', {}, [h('h3.section', {}, [icon('pad'), h('span', { text: ui.tr('ui.intro.controls') })]), controlsTable(ui)]),
    ]),
  ]);
}

// ---------- results ----------

function metricLines(ui: GameUi): string[] {
  const r = ui.result!;
  if (r.drill) {
    const known = ['time', 'speed', 'distance', 'slip', 'stance', 'missedCones'];
    return Object.entries(r.drill.metrics).filter(([k, v]) => known.includes(k) && Number.isFinite(v))
      .map(([k, v]) => ui.tr(`result.metric.${k}` as MessageKey, { value: v }));
  }
  return [];
}

function results(ui: GameUi): HTMLElement {
  const r = ui.result;
  if (!r) return h('section.screen.results');
  const title = r.failed ? ui.tr('results.failed')
    : r.ride.kind === 'drill' ? ui.tr('results.drill') : r.ride.kind === 'enduro' ? ui.tr('results.enduro') : r.ride.kind === 'trial' ? ui.tr('results.trial') : ui.tr('results.free');
  const rideName = r.ride.kind === 'drill' ? ui.tr(`drill.${r.ride.drill}.name` as MessageKey) : ui.tr(levelName(r.ride.level));
  const summary = r.drill ? ui.tr(`result.stars.${r.stars}` as MessageKey) : '';
  const lines = metricLines(ui);
  const reasons = r.drill?.reasons ?? [];
  const next = r.ride.kind === 'drill' && !r.failed ? nextDrill(r.ride.drill) : null;
  // A personal best or three stars: "Share your ride" leads (primary, first), the rest step back.
  const share = r.replayable && !r.failed && (r.newBest || r.stars === 3) ? videoButton(ui, true) : null;

  const laps = r.laps?.length ? h('div.laps', {}, [
    h('h3.section', {}, [icon('timer'), h('span', { text: ui.tr('results.laps') })]),
    h('ol.lap-list', {}, r.laps.map((l) => h(`li${l === Math.min(...r.laps!) ? '.best' : ''}`, { text: formatTime(l) }))),
    r.total !== undefined ? h('div.total', {}, [h('span', { text: ui.tr('results.total') }), h('b', { text: formatTime(r.total) })]) : null,
  ]) : r.total !== undefined ? h('div.laps', {}, [
    // A route without laps (enduro) still shows its time.
    h('div.total', {}, [h('span', { text: ui.tr('results.total') }), h('b', { text: formatTime(r.total) })]),
  ]) : null;

  return h('section.screen.results', {}, [
    h('div.results-wrap', {}, [
      h('div.panel.glass.results-card', {}, [
        h('div.eyebrow', { text: rideName }),
        h('h1.results-title', { text: title }),
        stars(r.stars, 3, true, 'big'),
        summary ? h('p.lead.center', { text: summary }) : null,
        r.newBest ? h('div.badge.gold', {}, [icon('trophy'), h('span', { text: ui.tr('results.newBest') })]) : null,
        // Stars and times count the same in both modes; the tag says which one the run was ridden in.
        ui.settings.beginner ? null : h('div.badge.mode', {}, [icon('pad'), h('span', { text: ui.tr('results.pro') })]),
        ...r.unlocked.map((l) => h('div.badge.unlock', {}, [icon('flag'), h('span', { text: ui.tr(l === 'track' ? 'unlock.track' : 'unlock.enduro') })])),
        lines.length ? h('ul.metrics', {}, lines.map((m) => h('li', { text: m }))) : null,
        laps,
        r.drill ? h('div.why', {}, [
          h('h3.section', {}, [icon('info'), h('span', { text: ui.tr('results.why') })]),
          reasons.length ? h('ul.reasons', {}, reasons.map((k) => h('li', { text: ui.tr(k) }))) : h('p.muted', { text: ui.tr('results.clean') }),
        ]) : null,
        share ? h('p.share-lead', { text: ui.tr('results.shareHint') }) : null,
        h('div.result-actions', {}, [
          share,
          next ? btn(ui.tr('ui.next'), () => { ui.ride = { level: 'ring', kind: 'drill', drill: next }; ui.dispatch({ type: 'restart' }); }, { primary: !share, ico: 'next' }) : null,
          btn(ui.tr('ui.retry'), () => ui.dispatch({ type: 'restart' }), { primary: !next && !share, ico: 'retry' }),
          r.replayable ? btn(ui.tr('ui.replay'), () => { if (ui.host!.replay()) ui.replayState(true); }, { ico: 'replay' }) : null,
          r.replayable && !share ? videoButton(ui) : null,
          btn(ui.tr('ui.toLevels'), () => ui.back(), { ico: 'flag' }),
        ]),
      ]),
      ctaCard(ui, r.ride.kind === 'drill'),
    ]),
  ]);
}

export function ctaCard(ui: GameUi, afterDrill = true): HTMLElement {
  return h('aside.cta', {}, [
    h('img.cta-logo', { attrs: { src: LOGO, alt: 'MegaMotocross' } }),
    h('div.cta-text', {}, [h('h2', { text: ui.tr('results.cta.title') }), h('p', { text: ui.tr(afterDrill ? 'results.cta.text' : 'results.cta.textRide') })]),
    h('div.cta-actions', {}, [
      h('a.btn.primary.dark', { attrs: { href: SITE, target: '_blank', rel: 'noopener', 'data-nav': '' } }, [icon('web'), h('span', { text: ui.tr('results.cta.book') })]),
      h('a.btn.light', { attrs: { href: WHATSAPP, target: '_blank', rel: 'noopener', 'data-nav': '' } }, [icon('chat'), h('span', { text: ui.tr('results.cta.whatsapp') })]),
      h('a.btn.light', { attrs: { href: `tel:${PHONE.replace(/\s/g, '')}`, 'data-nav': '' } }, [icon('phone'), h('span', { text: PHONE })]),
    ]),
  ]);
}

function replayBar(ui: GameUi): HTMLElement {
  return h('section.screen.replay', {}, [
    h('div.replay-badge', {}, [h('i.rec'), h('span', { text: ui.tr('ui.replaying') })]),
    h('div.replay-actions', {}, [videoButton(ui), btn(ui.tr('ui.stopReplay'), () => ui.host!.stopReplay(), { primary: true, ico: 'close' })]),
  ]);
}
